import React from "react";
import LeftSideBar from "../shared/LeftSideBar";
import LeftSideBarSpacing, { PaddingLessCol } from "../shared/LeftSideBarSpacing";
import TileStep from "../shared/TileStep";
import { ReactComponent as Union } from "../../assets/Union.svg";
import { ReactComponent as SelectOneSVG } from "../../assets/select-one-svg.svg";
import { ReactComponent as Zap } from "../../assets/zap.svg";
import { ReactComponent as SelectOneSingle } from "../../assets/SelectOneSingle.svg";
import { ReactComponent as SelectOneSingleLine } from "../../assets/longline.svg";
import SelectOneStep from "./SelectOneStep";
import { Cpu, Globe, Key } from "lucide-react";
import LiveWaitingIndicator from "../shared/LiveWaitingIndicator";
import useDashboardOnboardingStatus from "../../hooks/useDashboardOnboardingStatus";
import {
  API_KEYS_PATH,
  DOCS_AUTHENTICATION_PATH,
  DOCS_SDK_PATH,
  PROD_LINKED_ACCOUNTS_PATH,
} from "../../../../../router/RouterUtils";
import { Link, Text } from "@merge-api/merge-javascript-shared";
import FeatureCard from "../shared/FeatureCard";

const IntegrateSyncedDataSection = () => {
  const dashboardOnboardingStatus = useDashboardOnboardingStatus();
  return (
    <div className="flex flex-col mx-auto">
      <Text variant="h3" className="mb-6">
        Integrate synced data
      </Text>
      <div className="flex flex-row">
        <LeftSideBar stepNumber="1" isNormalStep />
        <TileStep
          img={<Union />}
          title="Connect backend to Unified API"
          subtitle={
            <>Integrate our Unified API with your backend to push and pull end user data.</>
          }
          stepNumber={1}
        />
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <div className="flex flex-col items-center justify-center w-full">
          <SelectOneSVG className="relative mt-2 -mb-[10px] max-[1000px]:hidden" />
          &nbsp;
        </div>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <div className="flex flex-col w-full">
          <div className="grid grid-cols-1 [grid-template-areas:'container'] max-[1000px]:grid-cols-[1fr_minmax(1px,4fr)] max-[1000px]:[grid-template-areas:'selectonesingle_...''selectoneline_container']">
            <SelectOneSingle className="hidden max-[1000px]:grid [grid-area:selectonesingle]" />
            <SelectOneSingleLine className="hidden max-[1000px]:grid max-[1000px]:relative max-[1000px]:left-10 [grid-area:selectoneline]" />
            <div className="grid [grid-area:container] grid-cols-2 gap-x-6 gap-y-[22px] justify-evenly max-[1000px]:grid-cols-1">
              <SelectOneStep
                img={<Cpu size={16} />}
                title="Direct API requests"
                subtitle={
                  <>
                    Make your own <strong>HTTPS requests</strong> to Merge's Unified API to push and
                    pull data from your backend.
                  </>
                }
                buttonName="API reference"
                docsUrl={DOCS_AUTHENTICATION_PATH}
              />
              <SelectOneStep
                img={<Globe size={16} />}
                title="SDKs"
                subtitle={
                  <>
                    Use our <strong>SDKs</strong> to connect to our Unified API quickly in an
                    opinionated way.
                  </>
                }
                buttonName="View SDKs"
                docsUrl={DOCS_SDK_PATH}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <div>&nbsp;</div>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <PaddingLessCol>
          <FeatureCard icon={<Key size={16} />} title="API keys and authentication">
            <Text>
              To <strong>interact</strong> with Merge’s API from your production environment, use
              your{" "}
              <Link href={API_KEYS_PATH} target="_blank">
                production access key
              </Link>
              .
            </Text>
            <Text>
              To <strong>authenticate</strong> your API requests to Merge, use your end users'
              Account Token. You can find the Account Token in the{" "}
              <Link href={PROD_LINKED_ACCOUNTS_PATH} target="_blank">
                {" "}
                Linked Accounts
              </Link>{" "}
              Dashboard.
            </Text>
            <Text>
              Learn more in our{" "}
              <Link href={DOCS_AUTHENTICATION_PATH} target="_blank">
                Authentication
              </Link>{" "}
              guide.
            </Text>
          </FeatureCard>
        </PaddingLessCol>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <PaddingLessCol>&nbsp;</PaddingLessCol>
      </div>
      <div className="flex flex-row">
        <LeftSideBarSpacing />
        <PaddingLessCol>&nbsp;</PaddingLessCol>
      </div>
      <div className="flex flex-row">
        <LeftSideBar stepNumber="2" isNormalStep={false} />
        <TileStep
          img={
            <div className="relative flex justify-center items-center bottom-[13px]">
              <Zap className="absolute" />
              <div className="absolute top-9">
                <LiveWaitingIndicator
                  color={dashboardOnboardingStatus?.has_made_request ? "green" : "red"}
                />
              </div>
            </div>
          }
          title="Test run"
          subtitle={
            <>
              <Text as="p">
                Once you've connected your backend, make any successful HTTP request to the Unified
                API.
              </Text>
              <Text as="p">We'll authenticate your API key and check this off your list!</Text>
            </>
          }
          stepNumber={2}
        />
      </div>
    </div>
  );
};

export default IntegrateSyncedDataSection;
